import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Search, CheckCircle2, RotateCcw, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** IST-aware default date: Today before 12:00, Tomorrow from 12:00 onwards. */
function defaultTargetDate() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  const istHour = Number(parts.hour);
  const todayIst = `${parts.year}-${parts.month}-${parts.day}`;
  if (istHour < 12) return { dateStr: todayIst, isTomorrow: false };
  const [y, m, d] = todayIst.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d) + 86400000);
  const dateStr = `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
  return { dateStr, isTomorrow: true };
}

const ATTENDANCE_RANK = { present: 2, completed: 2, no_show: 1, confirmed: 0 };

export default function QuickAttendance() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [{ dateStr, isTomorrow }] = useState(defaultTargetDate);
  const [summary, setSummary] = useState(null);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("qa_view") || "cards");

  useEffect(() => { localStorage.setItem("qa_view", view); }, [view]);

  const load = useCallback(() => {
    api.get(`/bookings/today-summary?date=${dateStr}`).then(({ data }) => setSummary(data));
  }, [dateStr]);
  useEffect(() => { load(); }, [load]);

  const allBookings = useMemo(() => {
    if (!summary) return [];
    const list = [];
    for (const info of Object.values(summary.by_pooja || {})) {
      for (const b of info.bookings) list.push(b);
    }
    return list;
  }, [summary]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? allBookings.filter((b) =>
          b.devotee_name.toLowerCase().includes(q) ||
          b.mobile.includes(q) ||
          b.pooja_name.toLowerCase().includes(q))
      : allBookings;
    return [...list].sort((a, b) => {
      const ra = ATTENDANCE_RANK[a.attendance_status] ?? 0;
      const rb = ATTENDANCE_RANK[b.attendance_status] ?? 0;
      if (ra !== rb) return ra - rb; // 0 (pending) first, 2 (present) last
      return a.devotee_name.localeCompare(b.devotee_name);
    });
  }, [allBookings, query]);

  const markPresent = async (id) => {
    setBusyId(id);
    try {
      await api.patch(`/bookings/${id}/attendance`, { attendance_status: "present" });
      toast.success(t("admin.presentMarked"));
      // Optimistic local update — also keep server truth.
      setSummary((s) => {
        if (!s) return s;
        const next = { ...s, by_pooja: { ...s.by_pooja } };
        for (const slug of Object.keys(next.by_pooja)) {
          next.by_pooja[slug] = {
            ...next.by_pooja[slug],
            bookings: next.by_pooja[slug].bookings.map((b) =>
              b.id === id ? { ...b, attendance_status: "present" } : b),
          };
        }
        return next;
      });
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const dayLabel = isTomorrow ? t("admin.tomorrow") : t("admin.today");
  const friendly = new Date(`${dateStr}T00:00:00+05:30`).toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div data-testid="quick-attendance-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl">{dayLabel} · {t("admin.quickAttendance")}</h1>
          <p className="text-sm opacity-65 mt-1">{friendly}{user?.role === "guruji" ? ` · ${user.assigned_poojas?.length || 0} ${t("admin.assignedPoojas")}` : ""}</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--border)] bg-white text-sm" data-testid="qa-refresh">
          <RotateCcw size={14} /> {t("admin.refresh")}
        </button>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("admin.search")}
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-[color:var(--border)] bg-white text-sm"
            data-testid="qa-search"
          />
        </div>
        <div className="inline-flex rounded-md border border-[color:var(--border)] bg-white overflow-hidden" data-testid="qa-view-toggle">
          <button
            onClick={() => setView("cards")}
            data-testid="qa-view-cards"
            className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 ${view === "cards" ? "bg-[color:var(--brand)] text-[#faf8f5]" : ""}`}
          >
            <LayoutGrid size={14} /> {t("admin.viewCards")}
          </button>
          <button
            onClick={() => setView("list")}
            data-testid="qa-view-list"
            className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 border-l border-[color:var(--border)] ${view === "list" ? "bg-[color:var(--brand)] text-[#faf8f5]" : ""}`}
          >
            <List size={14} /> {t("admin.viewList")}
          </button>
        </div>
      </div>

      {view === "cards" ? (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((b) => {
          const isPresent = b.attendance_status === "present" || b.attendance_status === "completed";
          return (
            <div
              key={b.id}
              data-testid={`qa-card-${b.id}`}
              className={`rounded-md border p-4 bg-white flex flex-col gap-3 transition-opacity ${isPresent ? "opacity-60" : ""}`}
            >
              <div>
                <div className="font-heading text-xl font-semibold leading-tight" data-testid={`qa-name-${b.id}`}>{b.devotee_name}</div>
                <div className="text-xs opacity-65 mt-1">{b.pooja_name}</div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <a href={`tel:${b.mobile}`} className="inline-flex items-center gap-1 text-[color:var(--brand)]" data-testid={`qa-call-${b.id}`}>
                  <Phone size={12} /> {b.mobile}
                </a>
                <span className={`px-2 py-0.5 rounded-full ${isPresent ? "bg-green-100 text-green-800" : "bg-[color:var(--bg-2)] opacity-80"}`}>
                  {t(`admin.attendance.${b.attendance_status}`)}
                </span>
              </div>
              {!isPresent && (
                <button
                  onClick={() => markPresent(b.id)}
                  disabled={busyId === b.id}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md btn-brand text-sm font-medium disabled:opacity-60"
                  data-testid={`qa-mark-present-${b.id}`}
                >
                  <CheckCircle2 size={14} /> {t("admin.markPresent")}
                </button>
              )}
              {isPresent && (
                <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-green-50 text-green-800 text-sm font-medium">
                  <CheckCircle2 size={14} /> {t("admin.attendance.present")}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 opacity-65 text-sm" data-testid="qa-empty">
            {query ? t("admin.noMatches") : t("admin.noBookings")}
          </div>
        )}
        </div>
      ) : (
        <div className="mt-6 bg-white border border-[color:var(--border)] rounded-md overflow-x-auto" data-testid="qa-list-view">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-[color:var(--bg-2)] text-left">
              <tr>
                <th className="p-3">{t("admin.devotee")}</th>
                <th className="p-3">{t("admin.pooja")}</th>
                <th className="p-3">{t("admin.mobile")}</th>
                <th className="p-3">{t("admin.attendanceCol")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const isPresent = b.attendance_status === "present" || b.attendance_status === "completed";
                return (
                  <tr key={b.id} className={`border-t border-[color:var(--border)] ${isPresent ? "opacity-60" : ""}`} data-testid={`qa-row-${b.id}`}>
                    <td className="p-3 font-semibold">{b.devotee_name}</td>
                    <td className="p-3 text-xs opacity-80">{b.pooja_name}</td>
                    <td className="p-3">
                      <a href={`tel:${b.mobile}`} className="inline-flex items-center gap-1 text-[color:var(--brand)]"><Phone size={12} />{b.mobile}</a>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isPresent ? "bg-green-100 text-green-800" : "bg-[color:var(--bg-2)]"}`}>
                        {t(`admin.attendance.${b.attendance_status}`)}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      {!isPresent ? (
                        <button onClick={() => markPresent(b.id)} disabled={busyId === b.id} className="px-3 py-1.5 rounded-md btn-brand text-xs inline-flex items-center gap-1 disabled:opacity-60" data-testid={`qa-list-present-${b.id}`}>
                          <CheckCircle2 size={12} /> {t("admin.markPresent")}
                        </button>
                      ) : (
                        <span className="text-xs text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> {t("admin.attendance.present")}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center opacity-65 text-sm">{query ? t("admin.noMatches") : t("admin.noBookings")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {user?.role === "admin" && (
        <div className="mt-8">
          <Link to="/admin" className="text-sm text-[color:var(--brand)] hover:underline" data-testid="qa-back-dashboard">
            ← {t("admin.dashboard")}
          </Link>
        </div>
      )}
      {user?.role === "guruji" && (
        <div className="mt-8">
          <Link to="/admin/home" className="text-sm text-[color:var(--brand)] hover:underline" data-testid="qa-back-guruji">
            ← {t("admin.dashboard")}
          </Link>
        </div>
      )}
    </div>
  );
}
